import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';
import { getCategoryLabel } from '@/components/feature/Breadcrumb';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="flex gap-3 p-3 rounded-lg hover:bg-background-100 transition-colors group cursor-pointer"
      >
        <div className="w-20 h-14 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={article.thumbnail}
            alt={article.title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2">
            {article.title}
          </h4>
          <p className="text-xs text-foreground-500 mt-1">{article.date} · {article.readTime}</p>
        </div>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all group cursor-pointer"
      >
        <div className="relative aspect-[16/9] overflow-hidden">
          <img
            src={article.thumbnail}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute top-3 left-3">
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-primary-500 text-background-50">
              {getCategoryLabel(article.category)}
            </span>
          </div>
        </div>
        <div className="p-5">
          <h3 className="text-lg font-heading font-bold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2">
            {article.title}
          </h3>
          <p className="text-sm text-foreground-600 mt-2 line-clamp-2">
            {article.description}
          </p>
          <div className="flex items-center gap-3 mt-3 text-xs text-foreground-500">
            <span>{article.date}</span>
            <span>·</span>
            <span>{article.readTime}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="flex flex-col sm:flex-row gap-4 p-4 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all group cursor-pointer"
    >
      <div className="w-full sm:w-48 h-32 flex-shrink-0 rounded-lg overflow-hidden">
        <img
          src={article.thumbnail}
          alt={article.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
      </div>
      <div className="flex-1 min-w-0 flex flex-col justify-center">
        <div className="flex items-center gap-2 mb-1">
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-700">
            {getCategoryLabel(article.category)}
          </span>
          <span className="text-xs text-foreground-500">{article.date}</span>
        </div>
        <h3 className="text-base font-heading font-bold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 mt-1 line-clamp-2">
          {article.description}
        </p>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          {article.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="px-2 py-0.5 rounded-md text-xs bg-secondary-100 text-secondary-700"
            >
              {tag}
            </span>
          ))}
          <span className="text-xs text-foreground-500">{article.readTime}</span>
        </div>
      </div>
    </Link>
  );
}